// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Radio, { RadioPresentationProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Tooltip, { TooltipPresentationProps } from "@stoneage/MobiusOverrides/Tooltip/Tooltip";
import { xParserOptions } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";

interface Props {
    label: string;
    value: string;
    tooltipText: string;
    options: { label: string; value: string }[];
    readOnly?: boolean;
    onChange: (value: string) => void;
    extendedStyles?: JettingAppRadioStyles;
}

export interface JettingAppRadioStyles {
    wrapper?: InjectableCss;
    radioGroup?: RadioGroupPresentationProps;
    radio?: RadioPresentationProps;
    tooltip?: TooltipPresentationProps;
}

export const jettingAppRadioStyles: JettingAppRadioStyles = {};

const JettingAppRadio = ({ extendedStyles, label, value, onChange, options, tooltipText, readOnly }: Props) => {
    const [styles] = useState(() => mergeToNew(jettingAppRadioStyles, extendedStyles));
    const [radioValue, setRadioValue] = useState(value);

    useEffect(() => {
        if (radioValue === value) {
            return;
        }
        setRadioValue(`${value}`);
    }, [value]);

    if (!options || !options.length) {
        return null;
    }

    const onRadioChange: React.ChangeEventHandler<HTMLInputElement> = event => {
        const value = event.currentTarget.value;
        setRadioValue(value);
        onChange && onChange(value);
    };

    const radioLabel = tooltipText ? (
        <>
            {label}
            <Tooltip {...styles.tooltip} passAsChildren={true}>
                <Typography>{parse(tooltipText, xParserOptions)}</Typography>
            </Tooltip>
        </>
    ) : (
        label
    );

    return (
        <StyledWrapper {...styles.wrapper}>
            <RadioGroup
                {...styles.radioGroup}
                horizontal={true}
                label={radioLabel}
                value={radioValue}
                disabled={readOnly}
                onChangeHandler={onRadioChange}
            >
                {options.map((option: any, index: number) => {
                    return (
                        <Radio key={index} {...styles.radio} value={option.value}>
                            {option.label}
                        </Radio>
                    );
                })}
            </RadioGroup>
        </StyledWrapper>
    );
};
export default JettingAppRadio;
